// FILE: src/services/learnerAuthService.js
//
// Thin wrappers over supabase.auth.* for the learner sign-in / sign-up flow.
// Each function returns { data, error } in a consistent shape so the modal
// can render the appropriate state without conditional spelunking.
//
// Email confirmation is enforced via the Supabase project setting — we don't
// override it here. The sign-up "check your email" UI handles the gap.

import { supabase } from './supabaseClient';
import { authRedirectUrl, stashAuthReturn } from './authReturn';

const RESULT = (data = null, error = null) => ({ data, error });

const errMessage = (err) =>
  err?.message || (typeof err === 'string' ? err : 'Unexpected error');

// ── Email + password ────────────────────────────────────────────────────────

export async function signUpWithEmail({ email, password, displayName }) {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        // display_name lands in user_metadata; the context reads it to seed
        // learner_profiles.display_name on first sign-in.
        data: displayName ? { display_name: displayName } : undefined,
        emailRedirectTo:
          typeof window !== 'undefined'
            ? `${window.location.origin}/?auth=confirmed`
            : undefined,
      },
    });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

export async function signInWithEmail({ email, password }) {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

/**
 * Re-send the signup confirmation email (activation Phase 2, 2026-08-12).
 * Used by the sign-in modal when Supabase rejects with "Email not confirmed" —
 * previously the learner's only path was digging out the original email
 * (1-hour link) or writing support. Server rate-limits apply (30/hr project
 * cap + per-address cooldown), so callers should disable the control after a
 * successful send.
 */
export async function resendConfirmationEmail(email) {
  try {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo:
          typeof window !== 'undefined'
            ? `${window.location.origin}/?auth=confirmed`
            : undefined,
      },
    });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

// ── OAuth (Google, Microsoft/Azure) ─────────────────────────────────────────

async function signInWithProvider(provider) {
  try {
    // Deep links must survive the provider round trip: a learner who opens a
    // session-review link from email signs in and comes back to THAT session,
    // never to a generic dashboard. authReturn keeps the destination in both
    // the redirect URL and sessionStorage.
    stashAuthReturn();
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: authRedirectUrl(),
      },
    });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

export const signInWithGoogle    = () => signInWithProvider('google');
export const signInWithMicrosoft = () => signInWithProvider('azure');

// ── Password reset ──────────────────────────────────────────────────────────

export async function sendPasswordResetEmail(email) {
  try {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo:
        typeof window !== 'undefined'
          ? `${window.location.origin}/?auth=reset`
          : undefined,
    });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

export async function updatePassword(newPassword) {
  try {
    const { data, error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return RESULT(null, { code: error.code || null, message: error.message });
    return RESULT(data, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}

// ── Sign out ────────────────────────────────────────────────────────────────

export async function signOut() {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) return RESULT(null, { code: null, message: error.message });
    return RESULT(true, null);
  } catch (err) {
    return RESULT(null, { code: null, message: errMessage(err) });
  }
}
